/* eslint-disable */
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";

import React, { useMemo } from "react";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Line,
  Path,
} from "@react-pdf/renderer";
import moment from "moment";
import turoLogoPng from "../../Assets/TURO_icon.png";
import checkmarkPng from "../../Assets/Vector 1.png";
import "../../font/registerSpartanPdf";
import "../../font/registerPatrickHandAndCaveatPdf";
import kalamFont from "../../font/Kalam-Light.ttf";
import { handDrawnCirclePath, HAND_DRAWN_CIRCLE_VIEWBOX } from "../../utils/handDrawnCircle";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });
// Source of truth for the 21 inspection points on the Turo Annual Vehicle
// Inspection Checklist. Labels are fixed by Figma (the API only drives the
// pass/fail status per index). Layout positions mirror Figma y-coordinates
// on the 1440x1925 canvas; the PDF renderer scales them to US Letter portrait.

export const INSPECTION_POINTS = [
  // Left column (points 1-12)
  { index: 1, text: "Brakes - shoes & pads (>3mm thick)", column: "left", y: 337 },
  { index: 2, text: "Emergency brake (parking brake)", column: "left", y: 398 },
  { index: 3, text: "Steering mechanism", column: "left", y: 459 },
  { index: 4, text: "Windshield", column: "left", y: 520, special: "windshield" },
  { index: 5, text: "Rear window and other glass", column: "left", y: 675 },
  { index: 6, text: "Windshield wipers", column: "left", y: 736 },
  { index: 7, text: "Headlights and tail lights", column: "left", y: 797 },
  { index: 8, text: "Turn indicator lights", column: "left", y: 858 },
  { index: 9, text: "Brake lights", column: "left", y: 919 },
  { index: 10, text: "Front seat adjustment", column: "left", y: 981 },
  { index: 11, text: "Doors (open, close, lock)", column: "left", y: 1042 },
  { index: 12, text: "Horn", column: "left", y: 1103 },
  // Right column (points 13-21)
  { index: 13, text: "Speedometer and warning lights", column: "right", y: 337 },
  { index: 14, text: "Bumpers", column: "right", y: 398 },
  { index: 15, text: "Muffler and exhaust system", column: "right", y: 459 },
  {
    index: 16,
    text: "Is the manufacture date for the\ntires less than 6 years old?",
    column: "right",
    y: 519,
  },
  {
    index: 17,
    text: 'Tires incl. tread depth (4/32" required)',
    column: "right",
    y: 580,
    special: "tires",
  },
  {
    index: 18,
    text: "Interior and exterior rear view mirrors",
    column: "right",
    y: 834,
  },
  {
    index: 19,
    text: "Seat belts for drivers and passenger (s)",
    column: "right",
    y: 895,
  },
  {
    index: 20,
    text: "Is the battery less than 5 years old?",
    column: "right",
    y: 956,
  },
  {
    index: 21,
    text: "If the battery is more than 5 years old,\n is the voltage greater than 12.1V \nusing a multimeter?",
    column: "right",
    y: 1016,
  },
];

export const TIRE_MEASUREMENTS = [
  { label: "Right front measurement", y: 629 },
  { label: "Left front measurement", y: 677 },
  { label: "Right rear measurement", y: 725 },
  { label: "Left rear measurement", y: 773 },
];

export const WINDSHIELD_SUBNOTES = [
  { text: 'Large crack (Greater than 1") = Fail', y: 569 },
  { text: 'Small crack (Smaller than 1") = Pass', y: 618 },
];

// PDF Scale: Figma canvas 1440×1925 → US Letter portrait (612×792 pts).
// Scale is limited by height: 792/1925 = 0.4114
// At this scale, width = 1440*0.4114 = 592.4pt (fits in 612pt with ~10pt margin each side)
export const FIGMA_TO_PDF_SCALE = 792 / 1925; // 0.4114

// Figma 1440×1925 → PDF US Letter portrait (612×792 pts)
const S = FIGMA_TO_PDF_SCALE; // 0.4114
const px = (n) => Number((n * S).toFixed(2));

// The content at scale fits in 592.4pt width; center it in 612pt page
const HORIZ_OFFSET = Number(((612 - 1440 * S) / 2).toFixed(2)); // ~9.8pt

// Helper: convert em-based letter-spacing to PDF points
const lsPx = (fontSizePx, emValue) => Number((fontSizePx * emValue * S).toFixed(2));

const buildPdfStyles = () => {
  const titleFontSize = 45;
  const colHeaderFontSize = 20;
  const pointFontSize = 22;
  const subnoteFontSize = 17;
  const tireLabelSize = 17;
  const tireValueSize = 18;
  const vboxTitleSize = 23;
  const vboxSubSize = 18;
  const vboxPTextSize = 22;
  const fieldValueSize = 24;
  const fieldLabelSize = 15;
  const mechanicTitleSize = 19;
  const footerSize = 15;

  return StyleSheet.create({
    page: {
      padding: 0,
      fontFamily: "spartanFont",
    },
    body: {
      position: "relative",
      width: px(1440),
      height: px(1925),
      marginLeft: HORIZ_OFFSET,
    },

    // Header — Turo logo
    logo: {
      position: "absolute",
      left: px(85),
      top: px(65),
      width: px(121),
      height: px(44),
      objectFit: "contain",
    },

    // Title
    title: {
      position: "absolute",
      left: px(85),
      top: px(163),
      width: px(1270),
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(titleFontSize),
      lineHeight: 1.1,
      color: "#000000",
    },

    // Inspection grid — two columns side by side
    inspectionGrid: {
      position: "absolute",
      left: px(85),
      top: px(255),
      width: px(1270),
      flexDirection: "row",
      justifyContent: "space-between",
    },
    inspectionCol: { width: px(610) },

    // Column header
    colHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      height: px(22),
      marginBottom: px(26),
      paddingRight: px(40),
    },
    colHeaderLabel: {
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(colHeaderFontSize),
      letterSpacing: lsPx(colHeaderFontSize, 0.13),
      textTransform: "uppercase",
      color: "#000000",
    },
    colHeaderPF: { 
      flexDirection: "row", 
      alignItems: "center",
      width: px(111),
      position: "relative",
      height: px(22),
    },
    colHeaderPass: {
      position: "absolute",
      left: px(-13),
      width: px(50),
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(colHeaderFontSize),
      letterSpacing: lsPx(colHeaderFontSize, 0.07),
      textTransform: "uppercase",
      color: "#606060",
      textAlign: "center",
    },
    colHeaderFail: {
      position: "absolute",
      left: px(74),
      width: px(50),
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(colHeaderFontSize),
      letterSpacing: lsPx(colHeaderFontSize, 0.07),
      textTransform: "uppercase",
      color: "#606060",
      textAlign: "center",
    },

    // Point row
    pointRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      minHeight: px(24),
      marginBottom: px(26),
      paddingRight: px(40),
    },
    pointRowRight: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      minHeight: px(24),
      marginBottom: px(14),
      paddingRight: px(40),
    },
    pointLabel: {
      flexDirection: "row",
      alignItems: "flex-start",
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(pointFontSize),
      lineHeight: 1.1,
      color: "#000000",
      flex: 1,
      maxWidth: px(500),
    },
    pointNumber: { width: px(20), marginRight: px(9) },
    pointCheckboxes: {
      flexDirection: "row",
      alignItems: "center",
    },
    checkboxContainer: {
      width: px(24),
      marginRight: px(63),
    },
    checkboxContainerLast: {
      width: px(24),
    },
    checkbox: {
      width: px(24),
      height: px(24),
      borderWidth: px(2),
      borderColor: "#000000",
      borderStyle: "solid",
      borderRadius: px(4),
      position: "relative",
    },

    // Windshield subnotes
    windshieldSubnotes: {
      marginLeft: px(18),
      marginBottom: px(12),
      minHeight: px(19),
      flexDirection: "row",
      alignItems: "center",
    },
    windshieldSubnoteLast: {
      marginBottom: px(20),
    },
    windshieldSubnoteText: {
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(subnoteFontSize),
      color: "#606060",
    },

    // Tires
    tireRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      minHeight: px(19),
      marginBottom: px(12),
      marginLeft: px(26),
    },
    tireRowLast: {
      marginBottom: px(20),
    },
    tireLabel: {
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(tireLabelSize),
      color: "#606060",
    },
    tireValueWrap: { width: px(111), alignItems: "center" },
    tireValue: {
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(tireValueSize),
      color: "#000000",
      width: "100%",
      textAlign: "center",
    },
    tireLine: {
      width: px(111),
      borderBottomWidth: px(2),
      borderBottomColor: "#000000",
      marginTop: px(-4),
    },

    // Vehicle inspection box
    vehicleBox: {
      position: "absolute",
      left: px(760),
      top: px(940),
      width: px(575),
      height: px(74),
      borderWidth: px(2),
      borderColor: "#000000",
      borderStyle: "solid",
      flexDirection: "row",
      alignItems: "center",
      paddingLeft: px(40),
      paddingRight: px(40),
    },
    vehicleBoxTitle: { flex: 1 },
    vehicleBoxTitleText: {
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(vboxTitleSize),
      letterSpacing: lsPx(vboxTitleSize, 0.07),
      textTransform: "uppercase",
      color: "#000000",
    },
    vehicleBoxSub: {
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(vboxSubSize),
      color: "#000000",
      marginTop: px(2),
    },
    vehicleBoxPF: {
      flexDirection: "row",
      alignItems: "center",
      marginLeft: px(30),
    },
    pfItem: {
      position: "relative",
      width: px(62),
      height: px(25),
      justifyContent: "center",
      alignItems: "center",
      marginRight: px(30),
    },
    pfItemLast: { marginRight: 0 },
    pfItemText: {
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(vboxPTextSize),
      textTransform: "uppercase",
      color: "#606060",
      zIndex: 2,
    },
    pfItemTextCircled: { color: "#000000" },
    pfCircleSvg: {
      position: "absolute",
      width: px(99),
      height: px(55),
      left: px(-18),
      top: px(-15),
      zIndex: 1,
    },

    // Host info section
    hostSection: {
      position: "absolute",
      left: px(85),
      top: px(1035),
     width: px(1270),
    },
    hostRow: {
      flexDirection: "row",
      marginBottom: px(0),
    },

    // Field
    field: { flexDirection: "column" },
    fieldValue: {
      /* fontFamily set inline dynamically */
      fontWeight: 800,
      fontSize: px(fieldValueSize),
      color: "#000000",
      paddingHorizontal: px(4),
      paddingBottom: 0,
      marginBottom: px(-3),
    },
    fieldLine: {
      borderBottomWidth: px(3),
      borderBottomColor: "#000000",
      borderBottomStyle: "solid",
      minHeight: px(35),
      justifyContent: "flex-end",
    },
    fieldLabel: {
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(fieldLabelSize),
      letterSpacing: lsPx(fieldLabelSize, 0.08),
      textTransform: "uppercase",
      color: "#000000",
      marginTop: px(12),
    },
    fieldName: { width: px(482) },
    fieldEmail: { width: px(758), marginLeft: px(30) },
    fieldSignature: { width: px(482) },
    fieldPhone: { width: px(563), marginLeft: px(30) },
    fieldDate: { width: px(167), marginLeft: px(28) },

    // Mechanic section
    mechanic: {
      position: "absolute",
      left: px(67),
      top: px(1190),
      width: px(1306),
      backgroundColor: "#F4F4F4",
      paddingHorizontal: px(18),
      paddingTop: px(20),
      paddingBottom: px(20),
    },
    mechanicTitle: {
      fontFamily: "spartanFont",
      fontWeight: 700,
      fontSize: px(mechanicTitleSize),
      letterSpacing: lsPx(mechanicTitleSize, 0.09),
      textTransform: "uppercase",
      color: "#000000",
      marginBottom: px(25),
    },
    mechanicRow: {
      flexDirection: "row",
      alignItems: "flex-end",
      marginBottom: px(0),
    },
    mechanicRowLast: {
      marginBottom: 0,
    },
    mPlate: { width: px(482) },
    mVin: { width: px(563), marginLeft: px(30) },
    mMileage: { width: px(169), marginLeft: px(28) },
    mMake: { width: px(482) },
    mModel: { width: px(563), marginLeft: px(30) },
    mYear: { width: px(167), marginLeft: px(28) },
    mCompany: { width: px(951) },
    mLicense: { width: px(291), marginLeft: px(28) },
    mInspector: { width: px(482) },
    mInspectorSig: { width: px(483), marginLeft: px(30) },
    mInspectorDate: { width: px(167), marginLeft: px(28) },

    // Footer
    footer: {
      position: "absolute",
      left: px(67),
      top: px(1570),
      width: px(1306),
      fontFamily: "spartanFont",
      fontWeight: 800,
      fontSize: px(footerSize),
      letterSpacing: lsPx(footerSize, 0.18),
      textTransform: "uppercase",
      color: "#000000",
      lineHeight: 1.13,
    },

    signatureImg: {
      width: px(160),
      height: px(60),
      objectFit: "contain",
    },
  });
};

// Component to render a checkbox
const PdfCheckbox = ({ checked, styles, isFirst }) => (
  <View style={isFirst ? styles.checkboxContainer : styles.checkboxContainerLast}>
    <View style={styles.checkbox}>
      {checked ? (
        <Image src={checkmarkPng} style={{ 
          position: "absolute",
          width: px(27), 
          height: px(22), 
          top: px(-5),
          left: px(3),
          objectFit: "contain" 
        }} />
      ) : null}
    </View>
  </View>
);

const PdfPointRow = ({ point, status, styles, isSpecialMargin, isRightColumn }) => {
  const pass = status === true || status === 1 || status === "1" || status === "pass";
  const fail = status === false || status === 0 || status === "0" || status === "fail";
  const baseStyle = isRightColumn ? styles.pointRowRight : styles.pointRow;
  const rowStyle = isSpecialMargin
    ? { ...baseStyle, marginBottom: isSpecialMargin }
    : baseStyle;
  return (
    <View style={rowStyle} wrap={false}>
      <View style={styles.pointLabel}>
        <Text style={styles.pointNumber}>{point.index}</Text>
        <Text>{point.text}</Text>
      </View>
      <View style={styles.pointCheckboxes}>
        <PdfCheckbox checked={pass} styles={styles} isFirst={true} />
        <PdfCheckbox checked={fail} styles={styles} isFirst={false} />
      </View>
    </View>
  );
};

const PdfCircledResult = ({ selected, children, styles, isLast }) => (
  <View style={isLast ? { ...styles.pfItem, ...styles.pfItemLast } : styles.pfItem}>
    {selected ? (
      <Svg style={styles.pfCircleSvg} viewBox={HAND_DRAWN_CIRCLE_VIEWBOX} fill="none">
        <Path
          d={handDrawnCirclePath}
          stroke="#000000"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    ) : null}
    <Text
      style={[
        styles.pfItemText,
        selected ? styles.pfItemTextCircled : null,
      ]}
    >
      {children}
    </Text>
  </View>
);

const Field = ({ value, label, styles, width, marginLeft, selectedFont }) => (
  <View style={width ? { ...styles.field, width: width, marginLeft: marginLeft || 0 } : { ...styles.field, flex: 1, marginLeft: marginLeft || 0 }}>
    <View style={styles.fieldLine}>
      <Text style={{ ...styles.fieldValue, fontFamily: selectedFont || "patrickHandFont" }}>
        {value || " "}
      </Text>
    </View>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const PdfInspectionColumn = ({ column, checksData, reportData, styles, selectedFont }) => {
  const points = INSPECTION_POINTS.filter((p) => p.column === column);
  const statusByIndex = (checksData || []).reduce((acc, c, i) => {
    acc[i] = c?.status;
    return acc;
  }, {});

  return (
    <View style={styles.inspectionCol}>
      <View style={styles.colHeader}>
        <Text style={styles.colHeaderLabel}>Inspection point</Text>
        <View style={styles.colHeaderPF}>
          <Text style={styles.colHeaderPass}>Pass</Text>
          <Text style={styles.colHeaderFail}>Fail</Text>
        </View>
      </View>
      {points.map((point, pointIdx) => (
        <React.Fragment key={point.index}>
          <PdfPointRow
            point={point}
            status={statusByIndex[point.index - 1]}
            styles={styles}
            isRightColumn={column === "right"}
            isSpecialMargin={
              point.special === "windshield" ? px(25) :
              point.special === "tires" ? px(25) :
              undefined
            }
          />
          {point.special === "windshield" &&
            WINDSHIELD_SUBNOTES.map((s, i) => (
              <View
                key={`w-${i}`}
                style={
                  i === WINDSHIELD_SUBNOTES.length - 1
                    ? { ...styles.windshieldSubnotes, ...styles.windshieldSubnoteLast }
                    : styles.windshieldSubnotes
                }
                wrap={false}
              >
                <Text style={styles.windshieldSubnoteText}>{s.text}</Text>
              </View>
            ))}
          {point.special === "tires" &&
            TIRE_MEASUREMENTS.map((m, i) => {
              const categoryKey = [
                "right_front_tire",
                "left_front_tire",
                "right_rear_tire",
                "left_rear_tire",
              ][i];
              const file = (reportData?.Files || []).find(
                (c) => c?.category === categoryKey || c?.category === "tire"
              ) || (checksData || []).find(
                (c) => c?.category === categoryKey || c?.category === "tire"
              );
              const dimension = file?.dimension;
              const isLast = i === TIRE_MEASUREMENTS.length - 1;
              return (
                <View
                  key={`t-${i}`}
                  style={
                    isLast
                      ? { ...styles.tireRow, ...styles.tireRowLast }
                      : styles.tireRow
                  }
                  wrap={false}
                >
                  <Text style={styles.tireLabel}>{m.label}</Text>
                  <View style={styles.tireValueWrap}>
                    <Text style={[styles.tireValue, { fontFamily: selectedFont || "patrickHandFont", fontSize: px(27), textAlign: "center" }]}>
                      {dimension === null || dimension === undefined
                        ? ""
                        : `${dimension}"`}
                    </Text>
                    <View style={styles.tireLine} />
                  </View>
                </View>
              );
            })}
        </React.Fragment>
      ))}
    </View>
  );
};

const TUROCertificateDownload = ({
  checksData,
  reportData,
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) => {
  const styles = useMemo(() => buildPdfStyles(), []);
  const selectedFont = handwritingFont;

  const formatDate = (d) =>
    d ? moment(new Date(d)).format("MM/ DD/ YYYY") : "";

  const hostName =
    reportData && `${reportData?.Username} ${reportData?.UserlastName || ""}`;
  const hostEmail = reportData?.Useremail;
  const hostPhone = reportData?.Userphone;
  const hostSig = reportData?.HostSignature;
  const inspectorName =
    reportData &&
    `${reportData?.Inspectorname} ${reportData?.InspectorlastName || ""}`;
  const inspectorSig = reportData?.Inspectorsignature;
  const reviewedDate = reportData?.reviewedDate;

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page
              size="LETTER"
              style={styles.page}
            >
              <View style={styles.body} wrap={false}>
                <Image style={styles.logo} src={turoLogoPng} />
                <Text style={styles.title}>Annual vehicle inspection checklist</Text>

                <View style={styles.inspectionGrid}>
                  <PdfInspectionColumn
                    column="left"
                    checksData={checksData}
                    reportData={reportData}
                    styles={styles}
                    selectedFont={selectedFont}
                  />
                  <PdfInspectionColumn
                    column="right"
                    checksData={checksData}
                    reportData={reportData}
                    styles={styles}
                    selectedFont={selectedFont}
                  />
                </View>

                <View style={styles.vehicleBox}>
                  <View style={styles.vehicleBoxTitle}>
                    <Text style={styles.vehicleBoxTitleText}>Vehicle inspection</Text>
                    <Text style={styles.vehicleBoxSub}>(please circle)</Text>
                  </View>
                  <View style={styles.vehicleBoxPF}>
                    <PdfCircledResult
                      selected={reportData?.finalStatus === "pass"}
                      styles={styles}
                    >
                      Pass
                    </PdfCircledResult>
                    <PdfCircledResult
                      selected={reportData?.finalStatus === "fail"}
                      styles={styles}
                      isLast
                    >
                      Fail
                    </PdfCircledResult>
                  </View>
                </View>

                <View style={styles.hostSection}>
                  <View style={{ ...styles.hostRow, marginBottom: px(10) }}>
                    <Field value={hostName} label="HOST NAME" width={px(482)} styles={styles} selectedFont={selectedFont} />
                    <Field value={hostEmail} label="HOST EMAIL ADDRESS" width={null} marginLeft={px(30)} styles={styles} selectedFont={selectedFont} />
                  </View>
                  <View style={styles.hostRow}>
                    <View style={{ ...styles.field, width: px(482) }}>
                      <View style={styles.fieldLine}>
                        {hostSig ? (
                          <Image
                            style={styles.signatureImg}
                            source={{ uri: `${process.env.REACT_APP_AWS_S3_LINK}/${hostSig}?x-request=xhr`, method: "GET", headers: { "Cache-Control": "no-cache" } }}
                          />
                        ) : <Text style={styles.fieldValue}> </Text>}
                      </View>
                      <Text style={styles.fieldLabel}>HOST SIGNATURE</Text>
                    </View>
                    <Field value={hostPhone} label="HOST PHONE NUMBER" width={px(563)} marginLeft={px(30)} styles={styles} selectedFont={selectedFont} />
                    <Field value={formatDate(reviewedDate)} label="DATE" width={null} marginLeft={px(28)} styles={styles} selectedFont={selectedFont} />
                  </View>
                </View>

                <View style={styles.mechanic} wrap={false}>
                  <Text style={styles.mechanicTitle}>
                    TO BE COMPLETED BY QUALIFIED MECHANIC
                  </Text>
                  <View style={styles.mechanicGrid}>
                    <View style={styles.mechanicRow}>
                      <Field value={reportData?.licensePlateNumber} label="LICENCE PLATE #" width={px(482)} styles={styles} selectedFont={selectedFont} />
                      <Field value={reportData?.vin} label="VIN #" width={px(563)} marginLeft={px(30)} styles={styles} selectedFont={selectedFont} />
                      <Field value={reportData?.milage} label="VEHICLE MILEAGE" width={null} marginLeft={px(28)} styles={styles} selectedFont={selectedFont} />
                    </View>
                    <View style={styles.mechanicRow}>
                      <Field value={reportData?.make} label="VEHICLE MAKE" width={px(482)} styles={styles} selectedFont={selectedFont} />
                      <Field value={reportData?.model} label="VEHICLE MODEL" width={px(563)} marginLeft={px(30)} styles={styles} selectedFont={selectedFont} />
                      <Field value={reportData?.year} label="VEHICLE YEAR" width={null} marginLeft={px(28)} styles={styles} selectedFont={selectedFont} />
                    </View>
                    <View style={{ ...styles.mechanicRow, justifyContent: "space-between", marginTop: px(15) }}>
                      <Field value={reportData && `Chex Inc. - ${reportData?.stateAddress}`} label="INSPECTOR COMPANY AND ADDRESS" width={px(950)} styles={styles} selectedFont={selectedFont} />
                      <Field value={reportData?.stateNumber} label="LICENCE/ASW ID # (OPTIONAL)" width={px(275)} styles={styles} selectedFont={selectedFont} />
                    </View>
                    <View style={{ ...styles.mechanicRow, marginBottom: 0 }}>
                      <Field value={inspectorName} label="INSPECTOR NAME" width={px(482)} styles={styles} selectedFont={selectedFont} />
                      <View style={{ ...styles.field, width: px(563), marginLeft: px(30) }}>
                        <View style={styles.fieldLine}>
                          {inspectorSig ? (
                            <Image
                              style={styles.signatureImg}
                              source={{ uri: `${process.env.REACT_APP_AWS_S3_LINK}/${inspectorSig}?x-request=xhr`, method: "GET", headers: { "Cache-Control": "no-cache" } }}
                            />
                          ) : <Text style={styles.fieldValue}> </Text>}
                        </View>
                        <Text style={styles.fieldLabel}>INSPECTOR SIGNATURE</Text>
                      </View>
                      <Field value={formatDate(reviewedDate)} label="DATE" width={null} marginLeft={px(28)} styles={styles} selectedFont={selectedFont} />
                    </View>
                  </View>
                </View>

                <Text style={styles.footer}>
                  Turo hosts: please ensure all fields on this checklist are completed and that your car passed the inspection before{"\n"}submitting it in your car's "safety inspections" tab.
                </Text>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ blob, url, loading, error }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div
                className="certificate-download-button"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    marginBottom: "20px",
                    marginTop: "20px",
                    color: "#3FA9FF",
                    fontWeight: "bold",
                    marginRight: "5px",
                    cursor: "pointer",
                  }}
                >
                  {linkLabel}
                </div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
};

const TUROCertificateDownloadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "template4");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <TUROCertificateDownload
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.turoVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setTuroReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(TUROCertificateDownloadConnected);
